import { useCallback } from 'react'
import { Select } from '@loadsmart/loadsmart-ui'
import { Button, ToggleGroup, Header, Icon } from '@loadsmart/miranda-react'
import styled from 'styled-components'
import moment from 'moment'

import { INVENTORY_VIEW_OPTIONS } from '../constants/reports'

import type { SiteDirectory } from '../../models/client/site'
import type { User } from '../../models/client/user'
import type { DateFilter, GateFeedFilter } from '../../clients/type'
import type { Location } from '../../models/client/reports'
import analytics, { AnalyticsEvent } from '../../util/analytics'
import { getAllowedSites } from '../utils/reports'
import { useObservationExports } from '../../hooks/useObservationExports'
import { useMessage } from '../../hooks/useMessage'

const ONE_MONTH = 33

const StyledSelect = styled(Select)`
  max-width: 210px;
`

type HeaderProps = {
  title: string
  user: User
  locations: SiteDirectory
  filters: GateFeedFilter[]
  selectedLocation: Location
  handleLocationChange: (location) => void
  onAddAsset: () => void
  displayDirection?: boolean
  handleAssetDirection?: (direction: string) => void
  isLoading: boolean
  isGateFeed?: boolean
  setIsExportDialogOpen?: (isOpen: boolean) => void
}

export function ReportHeader({
  title,
  user,
  locations,
  selectedLocation,
  filters,
  handleLocationChange,
  onAddAsset,
  handleAssetDirection,
  isLoading,
  displayDirection,
  isGateFeed,
  setIsExportDialogOpen,
}: HeaderProps) {
  const isYardReport = !isGateFeed

  const messenger = useMessage()
  const { exportData, isLoading: isExportLoading } = useObservationExports()

  const allowedSites = getAllowedSites(user, locations)
  const sites = isGateFeed
    ? [{ label: 'All locations', value: 'all' }, ...allowedSites]
    : allowedSites

  const handleExportData = useCallback(() => {
    const dateFilters: DateFilter[] = []
    filters.forEach((filter) => {
      if (filter.type === 'date') dateFilters.push(filter)
    })

    const [dateFilter] = dateFilters
    if (!dateFilter && isGateFeed) {
      messenger.danger('Please provide a date range to export.')
      return Promise.resolve()
    }

    if (isGateFeed) {
      const start = moment(dateFilter.value.start)
      const end = moment(dateFilter.value.end)
      if (moment.duration(end.diff(start)).asDays() > ONE_MONTH) {
        messenger.danger('Export date range exceed one month of data.')
        return Promise.resolve()
      }
    }

    try {
      exportData({
        filters,
        report: isGateFeed ? false : true,
      })
      analytics.track(AnalyticsEvent.ReportsExportData)
    } catch (e) {
      if (e instanceof Error) {
        // eslint-disable-next-line no-console
        console.error(`ERROR handleDataExport: ${e.message}`)
      }
      messenger.error(new Error('Something went wrong, could not export data'))
    } finally {
      return Promise.resolve()
    }
  }, [filters])

  const handleAddAsset = () => {
    onAddAsset()
  }

  return (
    <>
      <Header>
        <Header.Content>
          <Header.Title>{title}</Header.Title>
          <StyledSelect
            name="location"
            options={sites}
            value={selectedLocation ?? allowedSites[0]}
            onChange={handleLocationChange}
            hideClear
            disabled={isLoading}
          />
          {displayDirection && handleAssetDirection && (
            <ToggleGroup
              type="single-strict"
              options={INVENTORY_VIEW_OPTIONS}
              value="ingate"
              defaultValue="ingate"
              onChange={(e) => handleAssetDirection(e.target.value)}
              disabled={isLoading}
            />
          )}
        </Header.Content>
        <Header.Actions>
          {isYardReport ? (
            <Button
              variant="secondary"
              leading={<Icon name="download" size="16" />}
              onClick={() => setIsExportDialogOpen?.(true)}
              disabled={isLoading || isExportLoading}
            >
              Export data
            </Button>
          ) : (
            <Button
              variant="secondary"
              leading={<Icon name="download" size="16" />}
              onClick={handleExportData}
              disabled={isLoading || isExportLoading}
            >
              Export data
            </Button>
          )}
          <Button
            variant="secondary"
            leading={<Icon name="plus" size="16" />}
            onClick={handleAddAsset}
            disabled={isLoading}
          >
            Add asset
          </Button>
        </Header.Actions>
      </Header>
    </>
  )
}
